/**
 * useClienteForm - Hook for ClienteFormModal
 *
 * Manages:
 * - Form state and initialization (create/edit)
 * - Input masks (CNPJ, telefone, CEP)
 * - Field validation
 * - Submit handler (create/update)
 * - Delete handler
 * - Reactivate inactive cliente handler
 * - Estados/municípios loading via ViaCEP integration
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import clienteService, { Cliente, ClienteInput } from '../services/clienteService';
import apiClient from '../services/apiClient';
import useAuthStore from '../stores/authStore';
import { API_URL } from '../config/constants';

export interface Estado {
  codigo_uf: number;
  uf: string;
  nome: string;
}

export interface Municipio {
  codigo_ibge: number;
  nome: string;
  uf: string;
}

const DEFAULT_FORM_DATA: ClienteInput = {
  codigo: '',
  razao_social: '',
  nome_fantasia: '',
  cnpj: '',
  telefone: '',
  email: '',
  cep: '',
  endereco: '',
  bairro: '',
  cidade: '',
  estado: '',
  periodo_segregacao: 90,
  sinalizar_vencimento_1: 90,
  sinalizar_vencimento_2: 120,
  sinalizar_vencimento_3: 180,
  input_saldos_sistema_via_lotes: false,
  sinalizacao_auditiva_ativa: false,
  intervalo_vencimento_min_dias: undefined,
  intervalo_vencimento_max_dias: undefined,
  mensagem_log_beebagem: '',
  intervalo_agendamento_min_ativo: false,
  intervalo_agendamento_min_dias: undefined,
  intervalo_agendamento_max_ativo: false,
  intervalo_agendamento_max_dias: undefined,
  smtp_servidor: '',
  smtp_porta: 587,
  smtp_tls: true,
  email_copia: '',
  com_regime: false,
  sem_regime: false,
  ativo: true,
};

const buildFormFromCliente = (cliente: Cliente): ClienteInput => ({
  codigo: cliente.codigo,
  razao_social: cliente.razao_social,
  nome_fantasia: cliente.nome_fantasia || '',
  cnpj: cliente.cnpj || '',
  telefone: cliente.telefone || '',
  email: cliente.email || '',
  cep: cliente.cep || '',
  endereco: cliente.endereco || '',
  bairro: cliente.bairro || '',
  cidade: cliente.cidade || '',
  estado: cliente.estado || '',
  periodo_segregacao: cliente.periodo_segregacao ?? 90,
  // Sinalizar vencimento: preservar null (nao substituir por default) - e apenas alerta visual/sonoro
  sinalizar_vencimento_1: cliente.sinalizar_vencimento_1 ?? null,
  sinalizar_vencimento_2: cliente.sinalizar_vencimento_2 ?? null,
  sinalizar_vencimento_3: cliente.sinalizar_vencimento_3 ?? null,
  input_saldos_sistema_via_lotes: cliente.input_saldos_sistema_via_lotes || false,
  sinalizacao_auditiva_ativa: cliente.sinalizacao_auditiva_ativa || false,
  intervalo_vencimento_min_dias: cliente.intervalo_vencimento_min_dias,
  intervalo_vencimento_max_dias: cliente.intervalo_vencimento_max_dias,
  mensagem_log_beebagem: cliente.mensagem_log_beebagem || '',
  intervalo_agendamento_min_ativo: cliente.intervalo_agendamento_min_ativo || false,
  intervalo_agendamento_min_dias: cliente.intervalo_agendamento_min_dias,
  intervalo_agendamento_max_ativo: cliente.intervalo_agendamento_max_ativo || false,
  intervalo_agendamento_max_dias: cliente.intervalo_agendamento_max_dias,
  smtp_servidor: cliente.smtp_servidor || '',
  smtp_porta: cliente.smtp_porta || 587,
  smtp_tls: cliente.smtp_tls !== undefined ? cliente.smtp_tls : true,
  email_copia: cliente.email_copia || '',
  com_regime: cliente.com_regime || false,
  sem_regime: cliente.sem_regime || false,
  ativo: cliente.ativo,
});

// Input mask helpers
export const formatCNPJ = (value: string): string => {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 14) {
    return clean
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return value;
};

export const formatTelefone = (value: string): string => {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 11) {
    return clean
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }
  return value;
};

export const formatCEP = (value: string): string => {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 8) {
    return clean.replace(/^(\d{5})(\d)/, '$1-$2');
  }
  return value;
};

// CNPJ validation with check digits
export const validateCNPJComplete = (cnpj: string): boolean => {
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length !== 14) return false;
  if (/^(\d)\1+$/.test(clean)) return false;

  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(clean[i]) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit !== parseInt(clean[12])) return false;

  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(clean[i]) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return digit === parseInt(clean[13]);
};

interface UseClienteFormProps {
  isOpen: boolean;
  cliente?: Cliente | null;
  onSuccess: () => void;
  onSave?: () => void;
  onDelete?: (id: number) => void;
  onClose: () => void;
}

export function useClienteForm({
  isOpen,
  cliente,
  onSuccess,
  onSave,
  onDelete,
  onClose,
}: UseClienteFormProps) {
  const token = useAuthStore((state) => state.token);
  const isEditMode = !!cliente;

  const [formData, setFormData] = useState<ClienteInput>({
    ...DEFAULT_FORM_DATA,
    codigo: `CLI${Date.now()}`,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [clienteInativoInfo, setClienteInativoInfo] = useState<{ id: number; nome: string } | null>(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  // Load estados when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchEstados = async () => {
      try {
        const response = await axios.get<Estado[]>(
          `${API_URL}/estados`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEstados(response.data);
      } catch (err) {
        console.error('Erro ao carregar estados:', err);
      }
    };
    fetchEstados();
  }, [isOpen, token]);

  // Load municípios when estado changes
  useEffect(() => {
    const fetchMunicipios = async () => {
      if (!formData.estado) {
        setMunicipios([]);
        return;
      }
      try {
        const response = await axios.get<Municipio[]>(
          `${API_URL}/municipios/estado/${formData.estado}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMunicipios(response.data);
      } catch (err) {
        console.error('Erro ao carregar municípios:', err);
        setMunicipios([]);
      }
    };
    fetchMunicipios();
  }, [formData.estado, token]);

  // CEP auto-fill with debounce
  useEffect(() => {
    const buscarCepAutomatico = async () => {
      const cepLimpo = (formData.cep || '').replace(/\D/g, '');
      if (cepLimpo.length !== 8) return;
      try {
        setLoadingCep(true);
        // N227: /cep exige login; o apiClient renova o token vencido (axios direto dava 401 calado)
        const response = await apiClient.get(`/cep/${cepLimpo}`);
        if (response.data.error) return;
        setFormData(prev => ({
          ...prev,
          cep: response.data.cep,
          endereco: response.data.endereco,
          bairro: response.data.bairro,
          cidade: response.data.cidade,
          estado: response.data.estado,
        }));
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
      } finally {
        setLoadingCep(false);
      }
    };

    const timer = setTimeout(buscarCepAutomatico, 500);
    return () => clearTimeout(timer);
  }, [formData.cep]);

  // Initialize form data when cliente or isOpen changes
  useEffect(() => {
    if (cliente) {
      setFormData(buildFormFromCliente(cliente));
    } else {
      setFormData({ ...DEFAULT_FORM_DATA, codigo: `CLI${Date.now()}` });
    }
    setErrors({});
    setError('');
  }, [cliente, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.codigo.trim()) {
      newErrors.codigo = 'Código é obrigatório';
    }
    if (!formData.razao_social.trim()) {
      newErrors.razao_social = 'Razão Social é obrigatória';
    }
    if (formData.cnpj && !validateCNPJComplete(formData.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (formData.email_copia && formData.email_copia.trim()) {
      const emails = formData.email_copia.split(';').map(e => e.trim()).filter(e => e);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        newErrors.email_copia = `Emails inválidos: ${invalidEmails.join(', ')}`;
      }
    }
    if (formData.periodo_segregacao < 0 || formData.periodo_segregacao > 365) {
      newErrors.periodo_segregacao = 'Período deve estar entre 0 e 365 dias';
    }
    if (!formData.com_regime && !formData.sem_regime) {
      newErrors.com_regime = 'Selecione pelo menos um regime';
      toast.error('Selecione pelo menos um regime (Com Regime e/ou Sem Regime)');
    }
    if (formData.intervalo_agendamento_min_ativo && !formData.intervalo_agendamento_min_dias) {
      newErrors.intervalo_agendamento_min_dias = 'Informe o número mínimo de dias';
    }
    if (formData.intervalo_agendamento_max_ativo && !formData.intervalo_agendamento_max_dias) {
      newErrors.intervalo_agendamento_max_dias = 'Informe o número máximo de dias';
    }
    if (formData.intervalo_agendamento_min_dias && formData.intervalo_agendamento_min_dias < 1) {
      newErrors.intervalo_agendamento_min_dias = 'O intervalo mínimo deve ser maior que zero';
    }
    if (formData.intervalo_agendamento_max_dias && formData.intervalo_agendamento_max_dias < 1) {
      newErrors.intervalo_agendamento_max_dias = 'O intervalo máximo deve ser maior que zero';
    }
    if (
      formData.intervalo_agendamento_min_ativo &&
      formData.intervalo_agendamento_max_ativo &&
      formData.intervalo_agendamento_min_dias &&
      formData.intervalo_agendamento_max_dias &&
      formData.intervalo_agendamento_min_dias > formData.intervalo_agendamento_max_dias
    ) {
      newErrors.intervalo_agendamento_max_dias = 'O intervalo máximo deve ser maior ou igual ao mínimo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Verifique os erros no formulário');
      return;
    }

    // Guard defensivo: backend já valida (403), mas verificar aqui evita
    // network round-trip e exibe erro UX imediato. Espelha useHospitalFormModal.
    const { podeEditar, podeCriar } = useAuthStore.getState();
    const acao = isEditMode ? 'editar' : 'criar';
    const podeOperar = isEditMode
      ? podeEditar('CLIENTES_LIST')
      : podeCriar('CLIENTES_LIST');
    if (!podeOperar) {
      setError(
        `Voce nao tem permissao para ${acao} clientes. ` +
        `Solicite acesso ao administrador (Master).`
      );
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (isEditMode && cliente) {
        await clienteService.updateCliente(cliente.id, formData);
      } else {
        await clienteService.createCliente(formData);
      }
      toast.success(`Cliente ${isEditMode ? 'atualizado' : 'criado'} com sucesso!`);
      if (onSave) {
        onSave();
      } else {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      const responseData = err.response?.data;
      const detail = responseData?.detail;
      const status = err.response?.status;

      if (detail && typeof detail === 'object' && detail.error_type === 'CNPJ_CLIENTE_INATIVO') {
        setClienteInativoInfo({ id: detail.cliente_inativo_id, nome: detail.cliente_inativo_nome });
        setShowReactivateConfirm(true);
        toast.error(detail.error);
      } else if (status === 422 && Array.isArray(detail)) {
        const validationErrors = detail.map((e: any) => {
          const field = e.loc?.[e.loc.length - 1] || 'campo';
          const msg = e.msg || 'valor inválido';
          return `${field}: ${msg}`;
        }).join('; ');
        const errorMsg = `Erro de validação: ${validationErrors}`;
        setError(errorMsg);
        toast.error(errorMsg);
      } else {
        const errorMsg = typeof detail === 'string' ? detail : (detail?.error || 'Erro ao salvar cliente');
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!cliente?.id || !onDelete) return;
    setDeleteLoading(true);
    try {
      await onDelete(cliente.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Erro ao excluir cliente:', err);
      setError('Erro ao excluir cliente. Verifique se não há dados vinculados.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReactivateCliente = async () => {
    if (!clienteInativoInfo) return;
    setReactivateLoading(true);
    try {
      await clienteService.reactivateCliente(clienteInativoInfo.id);
      toast.success(`Cliente '${clienteInativoInfo.nome}' reativado com sucesso!`);
      setShowReactivateConfirm(false);
      setClienteInativoInfo(null);
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Erro ao reativar cliente';
      toast.error(errorMsg);
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let formattedValue = value;

    if (name === 'cnpj') formattedValue = formatCNPJ(value);
    else if (name === 'telefone') formattedValue = formatTelefone(value);
    else if (name === 'cep') formattedValue = formatCEP(value);

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : formattedValue,
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return {
    formData,
    errors,
    loading,
    error,
    loadingCep,
    estados,
    municipios,
    isEditMode,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteLoading,
    showReactivateConfirm,
    setShowReactivateConfirm,
    clienteInativoInfo,
    setClienteInativoInfo,
    reactivateLoading,
    handleChange,
    handleSubmit,
    handleDelete,
    handleReactivateCliente,
  };
}
